import {
  Alert,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { caseFileApi } from '../../api/case-file.api.js';
import { customersApi } from '../../api/customers.api.js';
import { CustomerVerificationReportButton } from '../../components/CompanyScreeningReportButton.js';
import { APP_LOCALE } from '../../utils/locale.js';

export function CustomerKycReports({
  customerId,
  isBusiness,
}: {
  customerId: string;
  isBusiness: boolean;
}): JSX.Element {
  const parties = useQuery({
    queryKey: ['verification-parties', customerId],
    queryFn: async () => customersApi.verificationParties(customerId),
    enabled: isBusiness,
    retry: false,
  });
  // Report access does not depend on the case file or the newest attempt. Its
  // saved profile dates are useful context, even when that attempt was refused.
  const caseFile = useQuery({
    queryKey: ['case-file', customerId],
    queryFn: () => caseFileApi.forCustomer(customerId),
    retry: false,
  });
  const checks = new Map<string, string>();
  for (const entry of caseFile.data ?? []) {
    if (entry.entryType !== 'SCREENING' || !Array.isArray(entry.data?.['profiles'])) continue;
    for (const value of entry.data['profiles'] as unknown[]) {
      if (value === null || typeof value !== 'object') continue;
      const profile = value as Record<string, unknown>;
      const date = profile['screenedAt'];
      if (typeof date !== 'string' || !Number.isFinite(Date.parse(date))) continue;
      const key = profile['entityType'] === 'COMPANY'
        ? 'company'
        : typeof profile['providerId'] === 'string' ? profile['providerId'] : null;
      if (key !== null && !checks.has(key)) checks.set(key, date);
    }
  }
  const formatDate = (date: string | undefined): string => date === undefined
    ? 'See report for check date'
    : `Recorded check: ${new Date(date).toLocaleDateString(APP_LOCALE, { timeZone: 'UTC' })}`;
  const subjectDate = isBusiness
    ? checks.get('company')
    : checks.size === 1 && !checks.has('company') ? [...checks.values()][0] : undefined;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h6" component="h3" gutterBottom>Existing KYC and screening reports</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Open or download the existing provider reports. A pending or unsuccessful new screening does
        not prevent access to these reports. Their original check dates still apply.
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        <CustomerVerificationReportButton customerId={customerId} company={isBusiness} />
        <Typography variant="body2" color="text.secondary">{formatDate(subjectDate)}</Typography>
      </Stack>
      {isBusiness ? (
        parties.isError ? (
          <Alert
            severity="error"
            action={<Button disabled={parties.isFetching} onClick={() => void parties.refetch()}>Retry</Button>}
          >
            The directors and owners could not be loaded. Their reports are unavailable until this lookup succeeds.
          </Alert>
        ) : parties.isPending ? (
          <Typography variant="body2" role="status">Loading directors and owners…</Typography>
        ) : parties.data.parties.length === 0 ? (
          <Alert severity="info">No directors or owners are linked to this customer's provider profile.</Alert>
        ) : (
          <TableContainer>
            <Table size="small" aria-label="Director and owner KYC reports" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Person</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Identity verification</TableCell>
                  <TableCell>Recorded check</TableCell>
                  <TableCell>Report</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parties.data.parties.map((party) => (
                  <TableRow key={party.providerId}>
                    <TableCell>{party.fullName || 'Name unavailable'}</TableCell>
                    <TableCell>{party.role}</TableCell>
                    <TableCell>{party.identityDecision || 'Unavailable'}</TableCell>
                    <TableCell>{formatDate(checks.get(party.providerId))}</TableCell>
                    <TableCell>
                      <CustomerVerificationReportButton
                        customerId={customerId}
                        personProviderId={party.providerId}
                        subjectName={party.fullName}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : null}
    </Box>
  );
}
